import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Play, ShieldAlert, Split } from 'lucide-react';
import { pentestsAPI } from '../../../../services/api';
import KaliTaskOutputViewer from '../../../common/KaliTaskOutputViewer';

/**
 * Differential probe launcher.
 *
 * Fires many near-identical requests that differ only in one injected value and
 * reads the DIFFERENCE rather than the responses. That is how a blind injection
 * is confirmed when the application reflects nothing back.
 *
 * Two things this form deliberately does not hide:
 *
 *   - The control payload is visible and editable. It is what separates a
 *     finding from noise, and burying it behind a preset would let an operator
 *     run a probe without one and trust the number that comes back.
 *   - The required level is computed by the backend from the payloads and shown
 *     before dispatch, so it is obvious WHY a probe needs Prove rather than the
 *     operator discovering it through a 409.
 */

const ORACLES = [
  { value: 'status', label: 'Status code', hint: 'The classic true/false tell. 200 vs 500.' },
  { value: 'length', label: 'Body length', hint: 'When everything answers 200 but the body size moves.' },
  { value: 'regex', label: 'Body pattern', hint: 'A string present in one population and not the other.' },
  { value: 'timing', label: 'Response time', hint: 'Needs a real gap. Under 500ms is treated as jitter.' },
];

const DEFAULT_PAYLOADS = [
  { value: "x' AND '1'='1", group: 'true' },
  { value: "x' AND '1'='2", group: 'false' },
  { value: 'x', group: 'control' },
];

// Mirrors app/services/http_probe.py. Advisory only: the backend derives the
// real answer and is the enforcer. Shown here so the operator is not surprised.
const EXTRACTION_RE =
  /\bunion\s+(all\s+)?select\b|\b(version|database|current_user|user|schema)\s*\(\s*\)|\binformation_schema\b|\bpg_sleep\b|\bsleep\s*\(|\bload_file\s*\(|\bgroup_concat\s*\(|\bxp_cmdshell\b|@@version/i;

export default function ProbeLauncher({ pentest }) {
  const [url, setUrl] = useState('');
  const [oracle, setOracle] = useState('status');
  const [oraclePattern, setOraclePattern] = useState('');
  const [rateLimit, setRateLimit] = useState(5);
  const [payloads, setPayloads] = useState(DEFAULT_PAYLOADS);
  const [identities, setIdentities] = useState([]);
  const [identityId, setIdentityId] = useState('');
  const [replayId, setReplayId] = useState('');
  const [scope, setScope] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [launched, setLaunched] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, i] = await Promise.all([
          pentestsAPI.scope.get(pentest.id),
          pentestsAPI.identities.list(pentest.id),
        ]);
        if (!alive) return;
        setScope(s);
        setIdentities(i);
        if (!url && s.targets?.length) {
          const first = s.targets.find((t) => t.kind === 'url') || s.targets[0];
          setUrl(`${first.value}?param=__PAYLOAD__`);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Could not load scope and identities.');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pentest.id]);

  const requiredLevel = useMemo(
    () => (payloads.some((p) => EXTRACTION_RE.test(p.value)) ? 'prove' : 'confirm'),
    [payloads],
  );

  const hasControl = payloads.some((p) => p.group === 'control' && p.value.trim());
  const groupCount = new Set(payloads.map((p) => p.group)).size;
  const markerPresent = url.includes('__PAYLOAD__');
  const canLaunch = url.trim() && markerPresent && hasControl && groupCount >= 2 && !busy
    && (oracle !== 'regex' || oraclePattern.trim());

  const setPayload = (idx, patch) =>
    setPayloads((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const launch = async () => {
    setBusy(true);
    setError(null);
    setLaunched(null);
    try {
      const res = await pentestsAPI.probes.launch(pentest.id, {
        url: url.trim(),
        payloads: payloads.filter((p) => p.value.trim() || p.group === 'control'),
        oracle,
        oracle_pattern: oracle === 'regex' ? oraclePattern.trim() : null,
        rate_limit: Number(rateLimit) || 5,
        identity_id: identityId ? Number(identityId) : null,
        replay_identity_id: replayId ? Number(replayId) : null,
      });
      setLaunched(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.reason) {
        // The engagement's own rules refused it. Say which raise unblocks it.
        setError(
          `${detail.reason}${detail.required_level
            ? `. Raise this target to ${detail.required_level} on the Pre-Engagement tab to allow it.`
            : ''}`,
        );
      } else {
        setError(typeof detail === 'string' ? detail : 'Could not launch the probe.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Differential probe</h3>
        <p className="mt-1 text-xs text-slate-500">
          Fires the same request many times, changing only the injected value, and scores how far
          apart the response populations are. Use it when the application reflects nothing back
          and the only signal is the difference between two answers.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
            Request URL
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://console.wasabisys.com/v1/account/security-contacts?BucketName=__PAYLOAD__"
            className="w-full rounded-md border border-slate-300 px-2.5 py-2 font-mono text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
          <p className={`mt-1 text-[11px] ${markerPresent ? 'text-slate-400' : 'text-red-600'}`}>
            {markerPresent
              ? 'Each payload replaces __PAYLOAD__.'
              : 'The URL must contain __PAYLOAD__, or every request would be identical and the probe would measure nothing.'}
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Oracle</label>
          <select
            value={oracle}
            onChange={(e) => setOracle(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          >
            {ORACLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-slate-400">
            {ORACLES.find((o) => o.value === oracle)?.hint}
          </p>
        </div>

        {oracle === 'regex' ? (
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Pattern</label>
            <input
              value={oraclePattern}
              onChange={(e) => setOraclePattern(e.target.value)}
              placeholder="admin panel"
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
              Rate limit (req/sec)
            </label>
            <input
              type="number"
              value={rateLimit}
              onChange={(e) => setRateLimit(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Payloads</label>
        <div className="space-y-1.5">
          {payloads.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={p.group}
                onChange={(e) => setPayload(i, { group: e.target.value })}
                className={`w-24 rounded-md border px-2 py-1.5 text-xs font-semibold
                  ${p.group === 'control'
                    ? 'border-wasabi-300 bg-wasabi-50 text-wasabi-700'
                    : 'border-slate-300 text-slate-700'}`}
              />
              <input
                value={p.value}
                onChange={(e) => setPayload(i, { value: e.target.value })}
                className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 font-mono text-xs focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
              />
              <button
                type="button"
                onClick={() => setPayloads((prev) => prev.filter((_, n) => n !== i))}
                className="text-xs text-slate-400 hover:text-red-600"
              >
                remove
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPayloads((prev) => [...prev, { value: '', group: 'true' }])}
          className="mt-2 text-xs font-medium text-wasabi-700 hover:text-wasabi-800"
        >
          + Add payload
        </button>
        {!hasControl && (
          <p className="mt-1.5 text-[11px] text-red-600">
            A payload in group <span className="font-mono">control</span> is required. It establishes
            what the response looks like when nothing is wrong; without it a separation score means nothing.
          </p>
        )}
      </div>

      {identities.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
              Act as identity
            </label>
            <select
              value={identityId}
              onChange={(e) => setIdentityId(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
            >
              <option value="">Unauthenticated</option>
              {identities.map((i) => (
                <option key={i.id} value={i.id} disabled={!i.usable}>
                  {i.label}{i.usable ? '' : ' (unusable)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 mb-1.5">
              <Split className="h-3.5 w-3.5" /> Replay as
            </label>
            <select
              value={replayId}
              onChange={(e) => setReplayId(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
            >
              <option value="">No replay</option>
              {identities.filter((i) => String(i.id) !== identityId).map((i) => (
                <option key={i.id} value={i.id} disabled={!i.usable}>{i.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-400">
              Repeats every request as a second account and diffs the answers. This is how
              cross-account access control gets tested.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
        <button
          type="button"
          disabled={!canLaunch}
          onClick={launch}
          className="inline-flex items-center gap-1.5 rounded-md bg-wasabi-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-wasabi-600 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run probe
        </button>

        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldAlert className="h-3.5 w-3.5" />
          Needs level
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold
            ${requiredLevel === 'prove'
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-wasabi-200 bg-wasabi-50 text-wasabi-700'}`}>
            {requiredLevel === 'prove' ? 'Prove' : 'Confirm'}
          </span>
          {requiredLevel === 'prove' && (
            <span className="text-slate-400">a payload reads a value back</span>
          )}
        </span>

        {scope && (
          <span className="text-xs text-slate-400">
            floor {scope.roe.exploit_level_floor} &middot; {payloads.length}
            {replayId ? ' x 2' : ''} requests
          </span>
        )}
      </div>

      {launched && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 text-sm font-semibold text-slate-900">
            Probe dispatched &middot; {launched.request_count} requests
            {launched.identity_label ? ` as ${launched.identity_label}` : ''}
            {launched.replay_identity_label ? `, replayed as ${launched.replay_identity_label}` : ''}
          </div>
          <KaliTaskOutputViewer taskId={launched.beacon_task_id} />
        </div>
      )}
    </div>
  );
}
